import type { Level, LevelOrString, WriteFn } from "pino";

import { bold, type Color, createColors, italic } from "colorette";
import dayjs from "dayjs";

interface LogRecord {
    module: string;
    msg: string;
    level: number;
    time: number;
    [prop: string]: unknown;
}

interface LevelConfig {
    levelName: Level;
    levelColor: Color;
}

const availableColors = createColors({ useColor: true });
const { white, bgRed, bgRedBright, yellow, green, blue, gray, cyan } = availableColors;

const levels: Record<number, LevelConfig> = {
    10: { levelName: "trace", levelColor: gray },
    20: { levelName: "debug", levelColor: blue },
    30: { levelName: "info", levelColor: green },
    40: { levelName: "warn", levelColor: yellow },
    50: { levelName: "error", levelColor: bgRedBright },
    60: { levelName: "fatal", levelColor: bgRed },
};

const timestampStyle = white;
const messageStyle = cyan;

export function createWrite(logLevel: LevelOrString, { mixin }: { mixin?: () => Record<string, unknown> } = {}) {
    function shouldWrite(level: number) {
        const levelConfig = Object.entries(levels).find(([_level, { levelName }]) => levelName === logLevel);
        if (!levelConfig) return true; // If log level is not set, write everything to console
        return parseInt(levelConfig[0]) <= level;
    }

    return function write({ time, level, msg, module, ...extra }: LogRecord) {
        if (!shouldWrite(level)) return;
        Object.assign(extra, mixin?.());

        const timestamp = dayjs(time).format("HH:mm:ss.SSS");
        const styledTimestamp = timestampStyle(timestamp);

        const levelName = levels[level].levelName;
        const styledLevelName = bold(levels[level].levelColor(levelName.toUpperCase()));

        const styledModule = italic(gray(module));
        const styledMessage = messageStyle(msg);

        const logMessage = `${styledTimestamp} ${styledLevelName} [${styledModule}]: ${styledMessage}`;

        //Use appropriate console methods for proper browser DevTools treatment
        switch (levelName) {
            case "fatal":
            case "error":
                console.error(logMessage, extra);
                break;
            case "warn":
                console.warn(logMessage, extra);
                break;
            case "info":
                console.info(logMessage, extra);
                break;
            case "debug":
                console.debug(logMessage, extra);
                break;
            case "trace":
                console.trace(logMessage, extra);
                break;
            default:
                console.log(logMessage, extra);
                break;
        }
    } as WriteFn;
}
